/**
 * Marketing-side vehicle catalogue and the one fare band the site quotes.
 *
 * EVERY FIGURE HERE WAS MEASURED AGAINST PRODUCTION, not estimated:
 * `POST https://api.shiftwagon.co.za/driver-management/api/public/fare-estimates`
 * (2026-08-05). Fares are deterministic — repeated calls for the same route
 * returned identical figures.
 *
 * Two things this file exists to prevent:
 *
 * 1. ADVERTISING WHAT CANNOT BE BOOKED. The pricing engine quotes seven vehicle
 *    classes, but a fare is not an offer. The app decides what a customer may
 *    actually book, and its `constants/vehicleCatalog.ts` filters
 *    `USER_FACING_VEHICLE_TYPES` down — so classes the engine happily prices are
 *    not orderable at all. This list is therefore the BOOKABLE set, never the
 *    priced set. `vehicleType` is the API's own identifier so a fallback row and
 *    a live row are the same vehicle, not two things that merely look alike.
 *
 *    Note the app treats `SEMI_TRUCK` as an alias of `HORSE_AND_TRAILER`
 *    (`normalizeVehicleType`), so the engine's seven are six real classes.
 *
 * 2. THE PAGE CONTRADICTING ITSELF. The estimate widget shows live API fares
 *    while the price band and fleet table are rendered from this file. If the
 *    two disagree the visitor sees two prices for one trip. `sampleFare` is
 *    therefore the *measured* fare for SAMPLE_ROUTE rather than the output of a
 *    rate model this file invents.
 *
 * TO REFRESH: re-run the sample route against the endpoint above and update
 * `sampleFare` plus SAMPLE_ROUTE. Do not hand-edit a single fare in isolation.
 */

export interface VehicleRate {
  /** The API's `vehicleType`. Fallback rows and live rows share this identity. */
  vehicleType: string;
  /** The API's `displayName`. */
  label: string;
  /** The API's `capacityKg`. Null where the class has no single published figure. */
  capacityKg: number | null;
  /** What this class is actually for. Marketing copy — owned here, not by the API. */
  example: string;
  /** Measured fare for SAMPLE_ROUTE. Used only when the estimate API is unreachable. */
  sampleFare: number;
  /**
   * Whether a customer can actually order this today.
   *
   * MIRRORS THE APP, which is the authority: `constants/vehicleCatalog.ts`
   * filters `USER_FACING_VEHICLE_TYPES` and currently admits `BAKKIE` only.
   * When the app admits another class, flip the same one here — the fares and
   * copy below are already measured and written, so enabling a vehicle is one
   * boolean rather than a research exercise.
   */
  bookable: boolean;
}

/**
 * Every class the pricing engine knows, bookable or not.
 *
 * `SEMI_TRUCK` is absent deliberately: the app folds it into
 * `HORSE_AND_TRAILER` (`normalizeVehicleType`), so treating it as a separate
 * class would list one vehicle twice. `normalizeVehicleType` below applies the
 * same rule to anything the API returns.
 */
export const VEHICLE_CATALOGUE: VehicleRate[] = [
  {
    vehicleType: 'BAKKIE',
    label: 'Bakkie',
    capacityKg: 800,
    example: 'Single items, small loads, urgent urban runs.',
    sampleFare: 592,
    bookable: true,
  },
  {
    vehicleType: 'PANEL_VAN',
    label: 'Panel Van',
    capacityKg: 1000,
    example: 'Appliances and one-room moves, enclosed against the weather.',
    sampleFare: 678,
    bookable: false,
  },
  {
    vehicleType: 'TRUCK_4_TON',
    label: '4 Ton Truck',
    capacityKg: 4000,
    example: 'Full home moves and bulky, awkward loads.',
    sampleFare: 876,
    bookable: false,
  },
  {
    vehicleType: 'MEDIUM_TRUCK',
    label: '8 Ton Truck',
    capacityKg: 8000,
    example: 'Large household moves, office relocations, palletised stock.',
    sampleFare: 1317,
    bookable: false,
  },
  {
    vehicleType: 'TRUCK_14_TON',
    label: '14 Ton Truck',
    capacityKg: 14000,
    example: 'Bulk stock, multi-room offices, construction materials.',
    sampleFare: 2060,
    bookable: false,
  },
  {
    vehicleType: 'HORSE_AND_TRAILER',
    label: 'Horse & Trailer',
    capacityKg: null,
    example: 'High-capacity linehaul and articulated freight.',
    sampleFare: 4760,
    bookable: false,
  },
];

/**
 * What a customer can order today — the set the estimate widget may price.
 *
 * A live API response is filtered through this, not just the offline fallback.
 * Without that, the engine's full catalogue reappears on the page the moment the
 * endpoint answers, whatever this file says, because the widget renders what the
 * API returns.
 */
export const RATE_CARD: VehicleRate[] = VEHICLE_CATALOGUE.filter((vehicle) => vehicle.bookable);

/** The app's alias rule, so an enabled Horse & Trailer is not listed twice. */
export function normalizeVehicleType(vehicleType: string): string {
  const normalized = String(vehicleType ?? '').trim().toUpperCase();
  return normalized === 'SEMI_TRUCK' || normalized === 'HORSE_TRAILER'
    ? 'HORSE_AND_TRAILER'
    : normalized;
}

export function isBookableVehicle(vehicleType: string): boolean {
  const normalized = normalizeVehicleType(vehicleType);
  return RATE_CARD.some((vehicle) => vehicle.vehicleType === normalized);
}

/**
 * The route the widget falls back to pricing. Distance and duration are the
 * API's own answer for these coordinates — the previous 27.6 km was 2.2 km out.
 */
export const SAMPLE_ROUTE = {
  pickup: 'Mdantsane, East London',
  dropoff: 'KuGompo City',
  distanceKm: 25.4,
  durationSec: 1281,
};

/**
 * The short-local bakkie band, and the only rate model the site states.
 *
 * Confirmed exact against production at seventeen distances from 3.2 km to
 * 18.6 km — every one matched `220 + 10 x km` to the rand. Above the band the
 * engine switches: at 25.4 km a bakkie is R592, not the R474 this model
 * predicts. The changeover sits between 18.6 km and 25.4 km and its exact value
 * is a business fact, not something to infer from sampling — so `WORKED_EXAMPLE_KM`
 * stays inside the range that was actually verified.
 *
 * Applies to the bakkie only. Other classes have their own bands, and this file
 * does not guess at them.
 */
export const BAKKIE_LOCAL_BAND = {
  base: 220,
  perKm: 10,
  verifiedToKm: 18.6,
};

/** The trip the price band prices. Inside the verified range, deliberately. */
export const WORKED_EXAMPLE_KM = 15;

/** Only valid inside BAKKIE_LOCAL_BAND. Verified: 15 km -> R370. */
export const bakkieLocalFare = (distanceKm: number): number =>
  Math.round(BAKKIE_LOCAL_BAND.base + BAKKIE_LOCAL_BAND.perKm * distanceKm);

/**
 * R1 072 — space as the thousands separator, per South African convention.
 *
 * en-ZA groups with a non-breaking space (U+00A0), not a comma, so replacing
 * commas alone left an invisible NBSP in the output. Normalising every group
 * separator to a plain space keeps the rendered figure identical to the literals
 * these values replaced, and keeps it matchable by ordinary string comparison.
 */
function formatThousands(value: number): string {
  return value.toLocaleString('en-ZA').replace(/[\s,]/g, ' ');
}

export const formatRand = (amount: number): string => `R${formatThousands(amount)}`;

export const formatCapacity = (capacityKg: number | null): string =>
  capacityKg ? `~${formatThousands(capacityKg)} kg` : 'Capacity varies';
